// PeerMesh — owns the RTCPeerConnection + DataChannel lifecycle per peer.
//
// Responsibilities:
//   - Initiate or accept a peer connection based on lexicographic tiebreak:
//     myPeerId < otherPeerId ⇒ I'm the offerer.
//   - Drive offer/answer/ICE through the signaling layer.
//   - Open a single DataChannel ('glacier') per peer with ordered=true.
//   - Set bufferedAmountLowThreshold for backpressure.
//   - Dispatch parsed envelopes to a single onMessage callback.
//   - Enforce LIMITS.PEER_MESH_CAP.
//
// We DON'T do here:
//   - The receiver-consent UI (F14.2 — uses onMessage hook to surface offers).
//   - File chunking (F11.1 — uses send() to push envelopes).
//   - Reconnect (deferred to the higher-layer file-transfer state machine).

import { parse, serialize } from './envelope.js';
import { LIMITS } from './limits.js';

const CHANNEL_LABEL = 'glacier';
const BUFFERED_LOW = 256_000;

// Default ICE config — STUN only. TURN is deferred per architecture.md.
const DEFAULT_ICE = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

export class PeerMesh {
  /**
   * @param {{
   *   code: string,
   *   myPeerId: string,
   *   iceServers?: RTCIceServer[],
   *   onMessage?: (env: import('./types.js').Envelope, fromPeerId: string) => void,
   *   onPeerChange?: (peerId: string, state: 'connecting'|'open'|'closed'|'failed') => void,
   *   send?: (toPeerId: string, sig: {type:'offer'|'answer'|'ice', payload:object}) => Promise<void>,
   * }} opts
   */
  constructor(opts) {
    if (!opts || !opts.code || !opts.myPeerId) {
      throw new TypeError('PeerMesh: code and myPeerId required');
    }
    this.code = opts.code;
    this.myPeerId = opts.myPeerId;
    this.iceServers = opts.iceServers || DEFAULT_ICE;
    this.onMessage = opts.onMessage || (() => {});
    this.onPeerChange = opts.onPeerChange || (() => {});
    // The signaling sender is injected so this class doesn't directly depend
    // on Firestore; the consumer hook wires it to `sendSignal(code, ...)`.
    this._sigSend = opts.send || (async () => {});
    /** @type {Map<string, {pc: RTCPeerConnection, channel: RTCDataChannel | null, isOfferer: boolean, queuedIce: RTCIceCandidateInit[]}>} */
    this._peers = new Map();
    // ICE candidates that arrived before the offer/answer set up the PC.
    /** @type {Map<string, RTCIceCandidateInit[]>} */
    this._pendingIce = new Map();
    this._closed = false;
  }

  /** Number of peers currently in the mesh (open or connecting). */
  get size() {
    return this._peers.size;
  }

  /** Expose the underlying RTCPeerConnection for tooling like rttProbe. */
  getConnection(peerId) {
    const e = this._peers.get(peerId);
    return e ? e.pc : null;
  }

  /**
   * The peer's DataChannel if it's open, else null. The channel lives on the
   * per-peer entry (set by _wireChannel) — NOT on the RTCPeerConnection — so
   * senders must ask the mesh for it rather than reaching into `pc`.
   */
  getChannel(peerId) {
    const e = this._peers.get(peerId);
    if (e && e.channel && e.channel.readyState === 'open') return e.channel;
    return null;
  }

  /**
   * Initiate (or wait for) a connection to `peerId`. The lexicographic
   * smaller id becomes the offerer.
   *
   * @param {string} peerId
   * @returns {Promise<boolean>}  true if (re)created, false if cap hit or
   *                              already-connected.
   */
  async connectTo(peerId) {
    if (this._closed) return false;
    if (!peerId || peerId === this.myPeerId) return false;
    if (this._peers.has(peerId)) return false;
    if (this._peers.size >= LIMITS.PEER_MESH_CAP) {
      return false;
    }
    const isOfferer = this.myPeerId < peerId;
    const pc = this._newPC(peerId);
    const entry = { pc, channel: null, isOfferer, queuedIce: [] };
    this._peers.set(peerId, entry);
    this.onPeerChange(peerId, 'connecting');

    if (isOfferer) {
      // We create the channel and the offer; remote side picks up the
      // channel via `ondatachannel`.
      const channel = pc.createDataChannel(CHANNEL_LABEL, { ordered: true });
      this._wireChannel(peerId, channel);
      entry.channel = channel;
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await this._sigSend(peerId, { type: 'offer', payload: { sdp: offer } });
      } catch (err) {
        this._teardown(peerId, 'failed');
        return false;
      }
    } else {
      // We're the answerer — set up `ondatachannel` and wait for the offer
      // to arrive via handleSignal().
      pc.addEventListener('datachannel', (ev) => {
        this._wireChannel(peerId, ev.channel);
        entry.channel = ev.channel;
      });
    }
    return true;
  }

  /**
   * Process a signaling envelope. The caller (a hook subscribing to
   * Firestore) decides which signals are relevant.
   *
   * @param {{from:string, type:'offer'|'answer'|'ice', payload:object}} sig
   */
  async handleSignal(sig) {
    if (this._closed || !sig || !sig.from) return;
    const peerId = sig.from;
    if (peerId === this.myPeerId) return;

    let entry = this._peers.get(peerId);
    if (!entry) {
      // Early ICE that arrives before the offer creates the PC — buffer it
      // so we can replay once the PC exists. Offers and answers are what
      // actually spin up the connection.
      if (sig.type === 'ice') {
        const cand = sig.payload && sig.payload.candidate;
        if (!cand) return;
        let pending = this._pendingIce.get(peerId);
        if (!pending) {
          pending = [];
          this._pendingIce.set(peerId, pending);
        }
        pending.push(cand);
        return;
      }
      // First contact: spin up a connection on demand. Tiebreak still applies
      // — if we'd be the offerer, ignore (we should have initiated, not them).
      if (this.myPeerId < peerId) return;
      if (this._peers.size >= LIMITS.PEER_MESH_CAP) return;
      const pc = this._newPC(peerId);
      entry = { pc, channel: null, isOfferer: false, queuedIce: [] };
      this._peers.set(peerId, entry);
      pc.addEventListener('datachannel', (ev) => {
        this._wireChannel(peerId, ev.channel);
        entry.channel = ev.channel;
      });
      // Drain any pre-PC ICE we buffered earlier — they'll wait inside
      // queuedIce until remoteDescription is set.
      const pending = this._pendingIce.get(peerId);
      if (pending) {
        entry.queuedIce.push(...pending);
        this._pendingIce.delete(peerId);
      }
      this.onPeerChange(peerId, 'connecting');
    }

    const { pc } = entry;

    try {
      if (sig.type === 'offer') {
        await pc.setRemoteDescription(sig.payload.sdp);
        // Drain any ICE candidates that arrived early.
        for (const c of entry.queuedIce) {
          try { await pc.addIceCandidate(c); } catch { /* ignore */ }
        }
        entry.queuedIce = [];
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await this._sigSend(peerId, { type: 'answer', payload: { sdp: answer } });
      } else if (sig.type === 'answer') {
        await pc.setRemoteDescription(sig.payload.sdp);
        for (const c of entry.queuedIce) {
          try { await pc.addIceCandidate(c); } catch { /* ignore */ }
        }
        entry.queuedIce = [];
      } else if (sig.type === 'ice') {
        const candidate = sig.payload.candidate;
        if (!candidate) return;
        // If remoteDescription not set yet, buffer until it is.
        if (!pc.remoteDescription || !pc.remoteDescription.type) {
          entry.queuedIce.push(candidate);
        } else {
          try { await pc.addIceCandidate(candidate); } catch { /* ignore */ }
        }
      }
    } catch (err) {
      // SDP errors are usually unrecoverable for this peer.
      this._teardown(peerId, 'failed');
    }
  }

  /**
   * Send an envelope. If `toPeerId` is omitted, broadcasts to every open
   * channel in the mesh. Returns the number of peers it was actually sent to.
   *
   * @param {import('./types.js').Envelope} env
   * @param {string} [toPeerId]
   * @returns {number}
   */
  send(env, toPeerId) {
    if (this._closed) return 0;
    const buf = serialize(env);
    let sent = 0;
    if (toPeerId) {
      const entry = this._peers.get(toPeerId);
      if (entry && entry.channel && entry.channel.readyState === 'open') {
        entry.channel.send(buf);
        sent = 1;
      }
      return sent;
    }
    for (const entry of this._peers.values()) {
      if (entry.channel && entry.channel.readyState === 'open') {
        try {
          entry.channel.send(buf);
          sent += 1;
        } catch {
          /* one bad channel shouldn't take down the broadcast */
        }
      }
    }
    return sent;
  }

  /** Disconnect a specific peer. */
  disconnect(peerId) {
    this._teardown(peerId, 'closed');
  }

  /** Tear down every connection. Safe to call multiple times. */
  close() {
    if (this._closed) return;
    this._closed = true;
    for (const peerId of Array.from(this._peers.keys())) {
      this._teardown(peerId, 'closed');
    }
  }

  // ── internals ──────────────────────────────────────────────────────────────

  _newPC(peerId) {
    const PC =
      (typeof globalThis !== 'undefined' && globalThis.RTCPeerConnection) ||
      (typeof window !== 'undefined' && window.RTCPeerConnection);
    if (!PC) {
      throw new Error('PeerMesh: RTCPeerConnection unavailable');
    }
    const pc = new PC({ iceServers: this.iceServers });
    pc.addEventListener('icecandidate', (e) => {
      if (!e.candidate) return; // null = gathering done
      // Convert to a serializable POJO — RTCIceCandidate isn't JSON-safe.
      const cand = typeof e.candidate.toJSON === 'function'
        ? e.candidate.toJSON()
        : {
            candidate: e.candidate.candidate,
            sdpMid: e.candidate.sdpMid,
            sdpMLineIndex: e.candidate.sdpMLineIndex,
            usernameFragment: e.candidate.usernameFragment,
          };
      this._sigSend(peerId, { type: 'ice', payload: { candidate: cand } }).catch(() => {
        /* signaling overflow handled elsewhere; ICE is best-effort */
      });
    });
    pc.addEventListener('connectionstatechange', () => {
      const s = pc.connectionState;
      if (s === 'failed' || s === 'disconnected' || s === 'closed') {
        this._teardown(peerId, s === 'failed' ? 'failed' : 'closed');
      }
    });
    return pc;
  }

  _wireChannel(peerId, channel) {
    try {
      channel.binaryType = 'arraybuffer';
    } catch {
      /* some mocks have no setter */
    }
    try {
      channel.bufferedAmountLowThreshold = BUFFERED_LOW;
    } catch {
      /* read-only on some mocks */
    }
    channel.addEventListener('open', () => {
      this.onPeerChange(peerId, 'open');
    });
    channel.addEventListener('close', () => {
      this._teardown(peerId, 'closed');
    });
    channel.addEventListener('error', () => {
      this._teardown(peerId, 'failed');
    });
    channel.addEventListener('message', (ev) => {
      let env;
      try {
        env = parse(ev.data);
      } catch {
        return; // drop malformed
      }
      try {
        this.onMessage(env, peerId);
      } catch {
        /* user callback shouldn't break the mesh */
      }
    });
  }

  _teardown(peerId, state) {
    this._pendingIce.delete(peerId);
    const entry = this._peers.get(peerId);
    if (!entry) return;
    this._peers.delete(peerId);
    try {
      if (entry.channel) entry.channel.close();
    } catch {
      /* ignore */
    }
    try {
      entry.pc.close();
    } catch {
      /* ignore */
    }
    this.onPeerChange(peerId, state);
  }
}
